import { useEffect, useRef, useState, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { v4 as uuidV4 } from 'uuid'

import Plus from '@/presentation/assets/svg/Plus.svg'
import TrashIcon from '@/presentation/assets/svg/TrashIcon.svg'
import Button from '@/presentation/components/Button'
import FormImage from '@/presentation/components/FormImage'
import FullscreenViewer from '@/presentation/components/FullscreenViewer'
import Header from '@/presentation/components/Header'
import ConfirmDeleteAlterationPopup from '@/presentation/pages/Alteration/components/ConfirmDeleteAlterationPopup'
import { ROUTES } from '@/services/constants/routes'
import useNavigateWithOrderCode from '@/services/hooks/useNavigateWithOrderCode'
import { useAlteration } from '@/services/providers/AlterationContext'
import { usePreview } from '@/services/providers/PreviewContext'
import { useRoom } from '@/services/providers/RoomContext'
import validateFileType from '@/services/utils/validateFileType'

const AlterationRegisterPage: React.FC = () => {
  const [showConfirmDeleteAlterationPopup, setConfirmDeleteAlterationPopup] =
    useState(false)

  const { t } = useTranslation()

  const navigate = useNavigateWithOrderCode()

  const { previews, setPreviews, thereIsAnImageBeingUploaded } = usePreview()
  const {
    pendingAlterations,
    alterationInProcess,
    makeAlteration,
    removeAlterationPhotoByFilePath,
    removeAlteration
  } = useAlteration()
  const { rooms } = useRoom()

  const imageInputRef = useRef<HTMLInputElement>(null)
  const isNavigatingAwayRef = useRef(false)

  const [showFullscreenViewer, setShowFullscreenViewer] = useState(false)
  const [fullscreenViewerIndex, setFullscreenViewerIndex] = useState(0)

  const fullscreenViewerContent = useMemo(() => {
    return previews.map(preview => {
      const isFile = preview.file && Object.keys(preview.file).length > 0
      return {
        filePath: isFile ? URL.createObjectURL(preview.file) : preview.filePath,
        isVideo: false
      }
    })
  }, [previews])

  const handleOpenFullscreenViewer = (index: number) => {
    setFullscreenViewerIndex(index)
    setShowFullscreenViewer(true)
  }

  const handleCloseFullscreenViewer = () => {
    setShowFullscreenViewer(false)
  }

  const handleItemCardClick = () => {
    if (!room || !item) return
    if (item.id === 'building') {
      navigate(`${ROUTES.ROOM_ITEM}/0/building`)
    } else if (item.id === 'general') {
      navigate(`${ROUTES.ROOM_ITEM}/${room.id}/general`)
    } else {
      navigate(`${ROUTES.ROOM_ITEM}/${room.id}/${item.id}`)
    }
  }

  const [description, setDescription] = useState<string>(
    alterationInProcess?.description || ''
  )
  const [done, setDone] = useState<boolean>(alterationInProcess?.done || false)
  const [errors, setErrors] = useState<{ [key: string]: string }>()

  const room = rooms.find(room => room.id === alterationInProcess?.room_id)

  const item = room?.items.find(
    item => item.id === alterationInProcess?.item_id
  )

  const handleVerifyIfFileIsEmpty = (file: File) => {
    if (!file) return true

    return Object.keys(file).length === 0 && file.constructor === Object
  }

  const handleAddImage = () => imageInputRef.current?.click()

  const handleImageAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (files && files?.length > 0) {
      const [file] = files

      if (!validateFileType(file)) {
        setErrors(state => ({
          ...state,
          previews: t('INVALID_IMAGE', 'A imagem enviada é inválida!')
        }))

        return
      }

      setPreviews(state => [
        ...state,
        {
          id: uuidV4(),
          file,
          filePath: '',
          hasRemotelyUploaded: false,
          uploadProgress: 0
        }
      ])
    }
  }

  const handleRemoveImage = (
    indexToRemove: number,
    filePathToRemove?: string
  ) => {
    setPreviews(state => state.filter((_, index) => index !== indexToRemove))

    filePathToRemove && removeAlterationPhotoByFilePath(filePathToRemove)
  }

  const handleConfirmAlteration = async () => {
    setErrors({})

    const actualErrors: { [key: string]: string } = {}

    if (!description) {
      actualErrors.description = t(
        'DETAIL_CHANGE_TO_CONTINUE',
        'É necessário detalhar a alteração para continuar'
      )
    }

    if (Object.keys(actualErrors).length > 0) {
      setErrors(actualErrors)

      return
    }

    if (room && item) {
      const uploadPhotoError = await makeAlteration({
        room_id: room?.id,
        room_name: room?.name,
        item_id: item?.id,
        item_name: item?.name,
        description,
        done,
        photos: previews,
        solicitation_group: alterationInProcess?.solicitation_group
      })

      if (!uploadPhotoError) {
        setPreviews([])
        isNavigatingAwayRef.current = true
        navigate(ROUTES.ALTERATION_PENDING_LIST)
      }

      actualErrors.previews = t(
        'ERROR_TO_UPLOAD_A_PHOTO',
        'Houve um erro ao enviar uma foto, tente novamente anexar as fotos'
      )

      setErrors(actualErrors)
    }
  }

  const handleRemoveAlteratiion = () => {
    removeAlteration(room?.id as string, item?.id as string)

    navigate(ROUTES.ALTERATION_PENDING_LIST)
  }

  const handleOptionChange = (value: string) => {
    if (value === '1') {
      setDone(true)
    } else {
      setDone(false)
    }
  }

  useEffect(() => {
    if (isNavigatingAwayRef.current) return

    if (!room) {
      navigate(ROUTES.MAIN)
    }

    if (!item) {
      navigate(ROUTES.ROOMS, true)
    }
  }, [item, navigate, room])

  useEffect(() => {
    setPreviews(
      alterationInProcess?.photos?.map(photo => ({
        ...photo,
        uploadProgress: 0
      })) || []
    )
  }, [])

  const isAlterationAlreadyRegistered = pendingAlterations.some(alteration => {
    return alteration?.room_id === room?.id && alteration?.item_id === item?.id
  })

  window.onbeforeunload = function () {
    return t(
      'SURE_WANT_TO_EXIT_CHANGES',
      'Você tem certeza que quer sair? Todo progresso de alterações será perdido!'
    )
  }

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <Header
        title={t('REGISTER_CHANGES', 'Registrar alterações')}
        deleteDivergenceButton={
          isAlterationAlreadyRegistered && (
            <img
              className="w-5 cursor-pointer"
              src={TrashIcon}
              onClick={() => setConfirmDeleteAlterationPopup(true)}
            />
          )
        }
      />

      <div className="flex w-full max-w-[682px] flex-col items-center px-4 py-2">
        <h1 className="mb-6 w-full text-left text-3xl font-bold text-primary">
          {t('NEW_CHANGE', 'Nova alteração')}
        </h1>

        <span className="mb-2 block w-full text-left text-gray-500">
          {t('SELECTED_ITEM', 'Item selecionado')}
        </span>

        <div
          onClick={handleItemCardClick}
          className="mb-4 box-border flex w-full cursor-pointer flex-col rounded-xl border border-gray-400 bg-white shadow-md transition-opacity hover:opacity-90"
        >
          <div
            className="h-20 w-full rounded-t-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${item?.image})` }}
          />

          <div className="flex flex-col items-start justify-start gap-y-2 rounded-b-xl border-t-0 p-4 text-left">
            <span className="text-xl font-semibold text-gray-600">
              {item?.id === 'building'
                ? item?.name
                : `${item?.name} | ${room?.name}`}
            </span>

            {item?.observation && (
              <span className="text-sm text-gray-500">{item?.observation}</span>
            )}
          </div>
        </div>

        <span className="mb-2 mt-4 block w-full text-left text-gray-500">
          {t('CHANGE_DONE', 'A alteração já foi realizada?')}
        </span>

        <div className="mb-4 flex w-full items-center justify-start gap-6">
          <div className="flex flex-row items-center gap-1">
            <input
              className="size-4 cursor-pointer rounded-md border border-gray-300 text-sm text-gray-800 focus-within:outline-primary"
              type="radio"
              value="1"
              checked={done}
              onChange={v => handleOptionChange(v.currentTarget.value)}
            />

            <span className="ml-1">{t('YES', 'Sim')}</span>
          </div>

          <div className="flex flex-row items-center gap-1">
            <input
              className="size-4 cursor-pointer rounded-md border border-gray-300 text-sm text-gray-800 focus-within:outline-primary"
              type="radio"
              value="0"
              checked={!done}
              onChange={v => handleOptionChange(v.currentTarget.value)}
            />

            <span className="ml-1">{t('NO', 'Não')}</span>
          </div>
        </div>

        <span className="mb-2 block w-full text-left text-gray-500">
          {t('DESCRIBE_CHANGE', 'Descreva a alteração')}
        </span>

        <textarea
          className="block min-h-[122px] w-full rounded-lg border-[1.5px] border-gray-400 p-2 text-base shadow-md focus:border-primary focus:outline-none"
          value={description}
          onChange={v => setDescription(v.currentTarget.value)}
        />

        <span className="mt-1 block w-full text-left text-error">
          {errors?.description}
        </span>

        <span className="mb-2 mt-4 block w-full text-left text-gray-500">
          {t('PHOTOS', 'Fotos')}
        </span>

        <div className="flex w-full flex-wrap items-center justify-start gap-4">
          {previews.map((preview, index) => (
            <FormImage
              key={preview.filePath}
              image={
                !handleVerifyIfFileIsEmpty(preview?.file)
                  ? preview?.file
                  : preview?.filePath
              }
              uploadProgress={preview.uploadProgress}
              handleClickWithoutImage={handleAddImage}
              handleClickWithImage={() =>
                handleRemoveImage(index, preview?.filePath)
              }
              hasRemotelyUploaded={preview.hasRemotelyUploaded}
              onClickThumbnail={() => handleOpenFullscreenViewer(index)}
            />
          ))}

          {previews.length < 5 && (
            <div
              onClick={handleAddImage}
              className="flex size-[72px] cursor-pointer items-center justify-center rounded-lg border-[1.5px] border-gray-400 bg-white"
            >
              <img src={Plus} />
            </div>
          )}
        </div>

        <input
          ref={imageInputRef}
          onChange={handleImageAdded}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/bmp"
          className="hidden"
        />

        <span className="mt-1 block w-full text-left text-error">
          {errors?.previews}
        </span>

        <Button
          className="mb-4 mt-8 w-full max-w-sm"
          onClick={handleConfirmAlteration}
          disabled={thereIsAnImageBeingUploaded}
        >
          {t('REGISTER_CHANGE', 'Registrar alteração')}
        </Button>
      </div>

      {showConfirmDeleteAlterationPopup && (
        <ConfirmDeleteAlterationPopup
          onCancel={() => setConfirmDeleteAlterationPopup(false)}
          onConfirmButton={() => handleRemoveAlteratiion()}
        />
      )}

      {showFullscreenViewer && (
        <FullscreenViewer
          startIndex={fullscreenViewerIndex}
          content={fullscreenViewerContent}
          onClose={handleCloseFullscreenViewer}
        />
      )}
    </div>
  )
}

export default AlterationRegisterPage
